import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  Grid2 as Grid,
  Paper,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { alpha } from '@mui/material/styles';
import Label from '../components/Label';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`theme-showcase-tabpanel-${index}`}
      aria-labelledby={`theme-showcase-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function ColorSwatch({ color, name }) {
  const theme = useTheme();
  return (
    <Box sx={{ textAlign: 'center', mb: 3 }}>
      <Paper
        elevation={0}
        sx={{
          width: 100,
          height: 100,
          bgcolor: color,
          mb: 1,
          boxShadow: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 1,
          mx: 'auto',
          color: theme.palette.getContrastText(color || '#fff'),
          border: color === '#fff' ? '1px solid #e0e0e0' : 'none',
        }}
      >
        {color}
      </Paper>
      <Typography variant='caption' color='text.secondary'>
        {name}
      </Typography>
    </Box>
  );
}

function ColorScale({ colorName, colorObject }) {
  return (
    <Grid
      size={{
        xs: 12,
        md: 6,
        lg: 4,
      }}
    >
      <Typography variant='subtitle1' gutterBottom>
        {colorName}
      </Typography>
      <Grid container spacing={1}>
        {Object.entries(colorObject).map(([key, value]) => {
          // Skip non-color properties
          if (typeof value !== 'string' || !value.startsWith('#')) return null;
          return (
            <Grid
              key={key}
              size={{
                xs: 6,
                sm: 4,
              }}
            >
              <ColorSwatch color={value} name={key} />
            </Grid>
          );
        })}
      </Grid>
    </Grid>
  );
}

export default function ThemeShowcase() {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Container maxWidth='lg'>
      <Typography variant='h3' component='h1' sx={{ mb: 4, mt: 4 }}>
        Theme Showcase
      </Typography>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label='theme showcase tabs'>
          <Tab label='Colors' />
          <Tab label='Typography' />
          <Tab label='Components' />
          <Tab label='App Colors' />
          <Tab label='Utilities' />
        </Tabs>
      </Box>
      {/* Colors Tab */}
      <TabPanel value={tabValue} index={0}>
        <Typography variant='h4' sx={{ mb: 4 }}>
          Color Palette
        </Typography>

        <Typography variant='h6' gutterBottom>
          Primary
        </Typography>
        <Grid container spacing={3} sx={{ mb: 6 }}>
          {Object.entries(theme.palette.primary).map(([key, value]) => {
            if (typeof value === 'string' && value.startsWith('#')) {
              return (
                <Grid key={key}>
                  <ColorSwatch color={value} name={`primary.${key}`} />
                </Grid>
              );
            }
            return null;
          })}
        </Grid>

        <Typography variant='h6' gutterBottom>
          Secondary
        </Typography>
        <Grid container spacing={3} sx={{ mb: 6 }}>
          {Object.entries(theme.palette.secondary).map(([key, value]) => {
            if (typeof value === 'string' && value.startsWith('#')) {
              return (
                <Grid key={key}>
                  <ColorSwatch color={value} name={`secondary.${key}`} />
                </Grid>
              );
            }
            return null;
          })}
        </Grid>

        <Typography variant='h6' gutterBottom>
          Status Colors
        </Typography>
        <Grid container spacing={3} sx={{ mb: 6 }}>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 3,
            }}
          >
            <Typography variant='subtitle1' gutterBottom>
              Success
            </Typography>
            <Grid container spacing={1}>
              {Object.entries(theme.palette.success).map(([key, value]) => {
                if (typeof value === 'string' && value.startsWith('#')) {
                  return (
                    <Grid
                      key={key}
                      size={{
                        xs: 6,
                        sm: 4,
                      }}
                    >
                      <ColorSwatch color={value} name={`success.${key}`} />
                    </Grid>
                  );
                }
                return null;
              })}
            </Grid>
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 3,
            }}
          >
            <Typography variant='subtitle1' gutterBottom>
              Error
            </Typography>
            <Grid container spacing={1}>
              {Object.entries(theme.palette.error).map(([key, value]) => {
                if (typeof value === 'string' && value.startsWith('#')) {
                  return (
                    <Grid
                      key={key}
                      size={{
                        xs: 6,
                        sm: 4,
                      }}
                    >
                      <ColorSwatch color={value} name={`error.${key}`} />
                    </Grid>
                  );
                }
                return null;
              })}
            </Grid>
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 3,
            }}
          >
            <Typography variant='subtitle1' gutterBottom>
              Warning
            </Typography>
            <Grid container spacing={1}>
              {Object.entries(theme.palette.warning).map(([key, value]) => {
                if (typeof value === 'string' && value.startsWith('#')) {
                  return (
                    <Grid
                      key={key}
                      size={{
                        xs: 6,
                        sm: 4,
                      }}
                    >
                      <ColorSwatch color={value} name={`warning.${key}`} />
                    </Grid>
                  );
                }
                return null;
              })}
            </Grid>
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 3,
            }}
          >
            <Typography variant='subtitle1' gutterBottom>
              Info
            </Typography>
            <Grid container spacing={1}>
              {Object.entries(theme.palette.info).map(([key, value]) => {
                if (typeof value === 'string' && value.startsWith('#')) {
                  return (
                    <Grid
                      key={key}
                      size={{
                        xs: 6,
                        sm: 4,
                      }}
                    >
                      <ColorSwatch color={value} name={`info.${key}`} />
                    </Grid>
                  );
                }
                return null;
              })}
            </Grid>
          </Grid>
        </Grid>

        <Typography variant='h6' gutterBottom>
          Greys
        </Typography>
        <Grid container spacing={3} sx={{ mb: 6 }}>
          {Object.entries(theme.palette.grey).map(([key, value]) => {
            if (typeof value === 'string' && value.startsWith('#')) {
              return (
                <Grid key={key}>
                  <ColorSwatch color={value} name={`grey.${key}`} />
                </Grid>
              );
            }
            return null;
          })}
        </Grid>

        <Typography variant='h6' gutterBottom>
          Background
        </Typography>
        <Grid container spacing={3} sx={{ mb: 6 }}>
          {Object.entries(theme.palette.background).map(([key, value]) => {
            if (typeof value === 'string') {
              return (
                <Grid key={key}>
                  <ColorSwatch color={value} name={`background.${key}`} />
                </Grid>
              );
            }
            return null;
          })}
        </Grid>

        <Typography variant='h6' gutterBottom>
          Text
        </Typography>
        <Grid container spacing={3} sx={{ mb: 6 }}>
          {Object.entries(theme.palette.text).map(([key, value]) => {
            if (typeof value === 'string') {
              return (
                <Grid key={key}>
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: theme.palette.background.paper,
                      borderRadius: theme.shape.borderRadius,
                      border: '1px solid',
                      borderColor: theme.palette.divider,
                    }}
                  >
                    <Typography variant='body1' color={value} sx={{ mb: 1 }}>
                      This is {key} text
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {`text.${key}: ${value}`}
                    </Typography>
                  </Paper>
                </Grid>
              );
            }
            return null;
          })}
        </Grid>
      </TabPanel>
      {/* Typography Tab */}
      <TabPanel value={tabValue} index={1}>
        <Typography variant='h4' sx={{ mb: 4 }}>
          Typography
        </Typography>

        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant='h1' gutterBottom>
            h1. Heading
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            Font Size: {theme.typography.h1.fontSize}, Font Weight: {theme.typography.h1.fontWeight}, Line Height:{' '}
            {theme.typography.h1.lineHeight}
          </Typography>

          <Typography variant='h2' gutterBottom>
            h2. Heading
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            Font Size: {theme.typography.h2.fontSize}, Font Weight: {theme.typography.h2.fontWeight}, Line Height:{' '}
            {theme.typography.h2.lineHeight}
          </Typography>

          <Typography variant='h3' gutterBottom>
            h3. Heading
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            Font Size: {theme.typography.h3.fontSize}, Font Weight: {theme.typography.h3.fontWeight}, Line Height:{' '}
            {theme.typography.h3.lineHeight}
          </Typography>

          <Typography variant='h4' gutterBottom>
            h4. Heading
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            Font Size: {theme.typography.h4.fontSize}, Font Weight: {theme.typography.h4.fontWeight}, Line Height:{' '}
            {theme.typography.h4.lineHeight}
          </Typography>

          <Typography variant='h5' gutterBottom>
            h5. Heading
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            Font Size: {theme.typography.h5.fontSize}, Font Weight: {theme.typography.h5.fontWeight}, Line Height:{' '}
            {theme.typography.h5.lineHeight}
          </Typography>

          <Typography variant='h6' gutterBottom>
            h6. Heading
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            Font Size: {theme.typography.h6.fontSize}, Font Weight: {theme.typography.h6.fontWeight}, Line Height:{' '}
            {theme.typography.h6.lineHeight}
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant='subtitle1' gutterBottom>
            subtitle1. Lorem ipsum dolor sit amet, consectetur adipisicing elit.
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            Font Size: {theme.typography.subtitle1.fontSize}, Font Weight: {theme.typography.subtitle1.fontWeight}, Line
            Height: {theme.typography.subtitle1.lineHeight}
          </Typography>

          <Typography variant='subtitle2' gutterBottom>
            subtitle2. Lorem ipsum dolor sit amet, consectetur adipisicing elit.
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            Font Size: {theme.typography.subtitle2.fontSize}, Font Weight: {theme.typography.subtitle2.fontWeight}, Line
            Height: {theme.typography.subtitle2.lineHeight}
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant='body1' gutterBottom>
            body1. Lorem ipsum dolor sit amet, consectetur adipisicing elit. Quos blanditiis tenetur unde suscipit, quam
            beatae rerum inventore consectetur, neque doloribus, cupiditate numquam dignissimos laborum fugiat deleniti?
            Eum quasi quidem quibusdam.
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            Font Size: {theme.typography.body1.fontSize}, Font Weight: {theme.typography.body1.fontWeight}, Line Height:{' '}
            {theme.typography.body1.lineHeight}
          </Typography>

          <Typography variant='body2' gutterBottom>
            body2. Lorem ipsum dolor sit amet, consectetur adipisicing elit. Quos blanditiis tenetur unde suscipit, quam
            beatae rerum inventore consectetur, neque doloribus, cupiditate numquam dignissimos laborum fugiat deleniti?
            Eum quasi quidem quibusdam.
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            Font Size: {theme.typography.body2.fontSize}, Font Weight: {theme.typography.body2.fontWeight}, Line Height:{' '}
            {theme.typography.body2.lineHeight}
          </Typography>

          <Typography variant='button' display='block' gutterBottom>
            button text
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            Font Size: {theme.typography.button.fontSize}, Font Weight: {theme.typography.button.fontWeight}, Line
            Height: {theme.typography.button.lineHeight}
          </Typography>

          <Typography variant='caption' display='block' gutterBottom>
            caption text
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            Font Size: {theme.typography.caption.fontSize}, Font Weight: {theme.typography.caption.fontWeight}, Line
            Height: {theme.typography.caption.lineHeight}
          </Typography>

          <Typography variant='overline' display='block' gutterBottom>
            overline text
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            Font Size: {theme.typography.overline.fontSize}, Font Weight: {theme.typography.overline.fontWeight}, Line
            Height: {theme.typography.overline.lineHeight}
          </Typography>
        </Paper>
      </TabPanel>
      {/* Components Tab */}
      <TabPanel value={tabValue} index={2}>
        <Typography variant='h4' sx={{ mb: 4 }}>
          Components
        </Typography>

        <Grid container spacing={4}>
          {/* Buttons */}
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Card>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Buttons
                </Typography>
                <Box sx={{ '& button': { m: 1 } }}>
                  <div>
                    <Button variant='contained' color='primary'>
                      Primary
                    </Button>
                    <Button variant='contained' color='secondary'>
                      Secondary
                    </Button>
                    <Button variant='contained' color='success'>
                      Success
                    </Button>
                    <Button variant='contained' color='error'>
                      Error
                    </Button>
                  </div>
                  <div>
                    <Button variant='outlined' color='primary'>
                      Primary
                    </Button>
                    <Button variant='outlined' color='secondary'>
                      Secondary
                    </Button>
                    <Button variant='outlined' color='success'>
                      Success
                    </Button>
                    <Button variant='outlined' color='error'>
                      Error
                    </Button>
                  </div>
                  <div>
                    <Button variant='text' color='primary'>
                      Primary
                    </Button>
                    <Button variant='text' color='secondary'>
                      Secondary
                    </Button>
                    <Button variant='text' color='success'>
                      Success
                    </Button>
                    <Button variant='text' color='error'>
                      Error
                    </Button>
                  </div>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Chips */}
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Card>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Chips
                </Typography>
                <Box sx={{ '& > *': { m: 0.5 } }}>
                  <Chip label='Default' />
                  <Chip label='Primary' color='primary' />
                  <Chip label='Secondary' color='secondary' />
                  <Chip label='Success' color='success' />
                  <Chip label='Warning' color='warning' />
                  <Chip label='Error' color='error' />
                  <Chip label='Info' color='info' />
                  <Divider sx={{ my: 1.5 }} />
                  <Chip label='Outlined Default' variant='outlined' />
                  <Chip label='Outlined Primary' color='primary' variant='outlined' />
                  <Chip label='Outlined Secondary' color='secondary' variant='outlined' />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Text Fields */}
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Card>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Text Fields
                </Typography>
                <Box sx={{ '& .MuiTextField-root': { m: 1, width: '25ch' } }}>
                  <div>
                    <TextField label='Standard' />
                    <TextField label='Filled' variant='filled' />
                    <TextField label='Outlined' variant='outlined' />
                  </div>
                  <div>
                    <TextField label='With Helper Text' helperText='Helper text' variant='outlined' />
                    <TextField error label='Error' helperText='Error message' variant='outlined' />
                  </div>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Other Form Elements */}
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Card>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Switches & Checkboxes
                </Typography>
                <Box>
                  <FormControlLabel control={<Switch defaultChecked />} label='Switch' />
                  <FormControlLabel control={<Switch />} label='Switch (off)' />
                  <Divider sx={{ my: 1.5 }} />
                  <FormControlLabel control={<Switch color='primary' defaultChecked />} label='Primary' />
                  <FormControlLabel control={<Switch color='secondary' defaultChecked />} label='Secondary' />
                  <FormControlLabel control={<Switch color='success' defaultChecked />} label='Success' />
                  <FormControlLabel control={<Switch color='warning' defaultChecked />} label='Warning' />
                  <FormControlLabel control={<Switch color='error' defaultChecked />} label='Error' />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
      {/* App Colors Tab */}
      <TabPanel value={tabValue} index={3}>
        <Typography variant='h4' sx={{ mb: 4 }}>
          Application-Specific Colors
        </Typography>

        <Grid container spacing={4}>
          {Object.entries(theme.palette.app || {}).map(([categoryName, colorObject]) =>
            typeof colorObject === 'object' && Object.keys(colorObject).length > 0 ? (
              <ColorScale key={categoryName} colorName={categoryName} colorObject={colorObject} />
            ) : null,
          )}

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Card>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Platform Colors
                </Typography>
                <Box sx={{ '& > *': { m: 0.5 } }}>
                  <Label
                    sx={{
                      bgcolor: alpha(theme.palette.app.platform.as, 0.16),
                      color: theme.palette.app.platform.as,
                    }}
                  >
                    Appstore Platform
                  </Label>
                  <Label
                    sx={{
                      bgcolor: alpha(theme.palette.app.platform.ps, 0.16),
                      color: theme.palette.app.platform.ps,
                    }}
                  >
                    Playstore Platform
                  </Label>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Card>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Subscription Status
                </Typography>
                <Box sx={{ '& > *': { m: 0.5 } }}>
                  {Object.entries(theme.palette.app.subscription).map(([key, color]) => (
                    <Label
                      key={key}
                      sx={{
                        bgcolor: alpha(color, 0.16),
                        color: color,
                      }}
                    >
                      {key}
                    </Label>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Card>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Transaction Status
                </Typography>
                <Box sx={{ '& > *': { m: 0.5 } }}>
                  {Object.entries(theme.palette.app.transaction).map(([key, color]) => (
                    <Label
                      key={key}
                      sx={{
                        bgcolor: alpha(color, 0.16),
                        color: color,
                      }}
                    >
                      {key}
                    </Label>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Card>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Consumption Status
                </Typography>
                <Box sx={{ '& > *': { m: 0.5 } }}>
                  {Object.entries(theme.palette.app.consumption).map(([key, color]) => (
                    <Label
                      key={key}
                      sx={{
                        bgcolor: alpha(color, 0.16),
                        color: color,
                      }}
                    >
                      {key}
                    </Label>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Card>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Delivery Status
                </Typography>
                <Box sx={{ '& > *': { m: 0.5 } }}>
                  {Object.entries(theme.palette.app.delivery).map(([key, color]) => (
                    <Label
                      key={key}
                      sx={{
                        bgcolor: alpha(color, 0.16),
                        color: color,
                      }}
                    >
                      {key}
                    </Label>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Card>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Refund Status
                </Typography>
                <Box sx={{ '& > *': { m: 0.5 } }}>
                  {Object.entries(theme.palette.app.refund).map(([key, color]) => (
                    <Label
                      key={key}
                      sx={{
                        bgcolor: alpha(color, 0.16),
                        color: color,
                      }}
                    >
                      {key}
                    </Label>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={12}>
            <Card>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Using App Colors in Components
                </Typography>
                <Box
                  component='pre'
                  sx={{
                    p: 2,
                    bgcolor: 'background.neutral',
                    borderRadius: 1,
                    overflow: 'auto',
                    fontSize: '0.875rem',
                  }}
                >
                  {`// Example: Platform label
<Label 
  sx={{ 
    bgcolor: alpha(theme.palette.app.platform.as, 0.16),
    color: theme.palette.app.platform.as
  }}
>
  Appstore App
</Label>

// Example: Subscription status
<Typography sx={{ color: theme.palette.app.subscription.active }}>
  Active Subscription
</Typography>

// Example: Transaction indicator
const getTransactionColor = (status) => theme.palette.app.transaction[status] || theme.palette.grey[500];`}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
      {/* Utilities Tab */}
      <TabPanel value={tabValue} index={4}>
        <Typography variant='h4' sx={{ mb: 4 }}>
          Theme Utilities
        </Typography>

        <Grid container spacing={4}>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Card>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Spacings
                </Typography>
                <Box>
                  {[0, 1, 2, 3, 4, 5, 8, 12].map((spacing) => (
                    <Box
                      key={spacing}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: theme.spacing(spacing),
                          height: 20,
                          bgcolor: theme.palette.primary.main,
                          mr: 2,
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant='body2'>
                        theme.spacing({spacing}) = {theme.spacing(spacing)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Card>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Shape
                </Typography>
                <Box>
                  <Typography variant='body2' gutterBottom>
                    Border Radius: {theme.shape.borderRadius}px
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    {[0, 1, 2, 4, 8, 16, 24].map((radius) => (
                      <Box
                        key={radius}
                        sx={{
                          width: 80,
                          height: 80,
                          bgcolor: theme.palette.primary.main,
                          borderRadius: radius,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: theme.palette.primary.contrastText,
                        }}
                      >
                        {radius}px
                      </Box>
                    ))}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Card>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Transitions
                </Typography>
                <Box>
                  <Typography variant='body2' gutterBottom>
                    Durations:
                  </Typography>
                  <Typography variant='body2' component='div'>
                    <ul>
                      <li>shortest: {theme.transitions.duration.shortest}ms</li>
                      <li>shorter: {theme.transitions.duration.shorter}ms</li>
                      <li>short: {theme.transitions.duration.short}ms</li>
                      <li>standard: {theme.transitions.duration.standard}ms</li>
                      <li>complex: {theme.transitions.duration.complex}ms</li>
                      <li>enteringScreen: {theme.transitions.duration.enteringScreen}ms</li>
                      <li>leavingScreen: {theme.transitions.duration.leavingScreen}ms</li>
                    </ul>
                  </Typography>

                  <Typography variant='body2' gutterBottom>
                    Easings:
                  </Typography>
                  <Typography variant='body2' component='div'>
                    <ul>
                      <li>easeInOut: {theme.transitions.easing.easeInOut}</li>
                      <li>easeOut: {theme.transitions.easing.easeOut}</li>
                      <li>easeIn: {theme.transitions.easing.easeIn}</li>
                      <li>sharp: {theme.transitions.easing.sharp}</li>
                    </ul>
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Card>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Shadows
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {[0, 1, 2, 4, 8, 12, 16, 24].map((elevation) => (
                    <Paper
                      key={elevation}
                      elevation={elevation}
                      sx={{
                        p: 2,
                        height: 60,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      elevation={elevation}
                    </Paper>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
    </Container>
  );
}
